import { computed, action, observable, reaction } from 'mobx'
import { task } from 'mobx-task'
import { validationContext, func } from 'validx'

/**
 * View Model for rating a pro.
 */
export default class RateProDialogViewModel {
  @observable
  showing = false

  @observable
  feedback = ''

  @observable
  rating = 0

  @observable
  thumbsRating = undefined

  @observable
  proRatingStatus = 'UNKNOWN'

  constructor({ projectDetailsStore, jobRatingStore, flashMessageStore }) {
    this.projectDetailsStore = projectDetailsStore
    this.jobRatingStore = jobRatingStore
    this.flashMessageStore = flashMessageStore
  }

  validation = validationContext(this, {
    feedback: [
      func(({ value }) => {
        return (
          value.length <= 280 || "Feedback can't be more than 280 characters"
        )
      }),
    ],
  })

  @computed
  get canRatePro() {
    return this.proRatingStatus === 'PENDING' && !!this.proChampion
  }

  @computed
  get proChampion() {
    return this.projectDetailsStore.project?.provider ?? {}
  }

  @computed
  get validRating() {
    return this.rating >= 1 && this.rating <= 5
  }

  @action.bound
  show() {
    if (!this.projectDetailsStore.canRatePro) {
      return
    }
    this.showing = true
  }

  @action.bound
  close() {
    this.showing = false
    this.reset()
  }

  @action.bound
  activate() {
    this.progressionStatusChangeDisposer = reaction(
      () => this.projectDetailsStore.isInOrPastDraftApproved,
      (_) => {
        if (!this.projectDetailsStore.jobProgressionFlowEnabled) {
          return
        }

        this.projectDetailsStore.fetchJobRatingStatus().then(() => {
          if (!this.projectDetailsStore.canRatePro) {
            return
          }

          this.show()
        })
      },
      {
        fireImmediately: true,
      }
    )

    this.jobStatusChangeDisposer = reaction(
      () =>
        this.projectDetailsStore.project &&
        this.projectDetailsStore.project.status,
      (value) => {
        if (
          this.projectDetailsStore.jobProgressionFlowEnabled ||
          value !== 'CLOSED'
        ) {
          return
        }

        this.projectDetailsStore.fetchJobRatingStatus().then(() => {
          if (!this.projectDetailsStore.canRatePro) {
            return
          }

          this.show()
        })
      },
      {
        fireImmediately: true,
      }
    )
  }

  @action.bound
  deactivate() {
    this.progressionStatusChangeDisposer?.()
    this.jobStatusChangeDisposer?.()
  }

  @action.bound
  setRating(value) {
    this.rating = value
  }

  @action.bound
  setThumbsRating(value) {
    this.thumbsRating = value
    this.rating = this.thumbsRating ? 4 : 2
  }

  @action.bound
  setProRatingStatus(status) {
    this.proRatingStatus = status
  }

  @action.bound
  reset() {
    this.rating = 0
    this.feedback = ''
    this.thumbsRating = undefined
    this.validation.reset()
  }

  submitRating = task.resolved(async () => {
    if (!this.validRating || !this.validation.reset().validate().isValid) {
      return
    }

    const msg = this.flashMessageStore.create({
      message: 'Submitting...',
      inProgress: true,
    })

    try {
      await this.jobRatingStore.submitProRating(
        this.projectDetailsStore.project.id,
        this.rating,
        this.feedback,
        this.proChampion.userPublicId
      )
      msg.done('Thank you for rating your Tax Pro!').autoDismiss(4000)
      this.setProRatingStatus('RATED')
    } catch (error) {
      console.error('Error submitting pro rating', error)
      msg.failed('Error submitting pro rating. Please try again.')
    }

    this.close()
  })

  showJobRating = task.resolved(async () => {
    this.submitRating()

    this.projectDetailsStore.ratePlatformDialogViewModel.show()
    this.close()
  })
}
